// Chains, addresses and selectors used by the direct on-chain reads.

export const MAINNET = 4663;
export const TESTNET = 46630;
export type ChainId = typeof MAINNET | typeof TESTNET;

export interface ChainInfo {
  name: string;
  hex: string;
  rpcs: readonly string[];
  explorer: string;
}

export const CHAINS: Record<ChainId, ChainInfo> = {
  [MAINNET]: {
    name: 'Robinhood Chain mainnet',
    hex: '0x1237',
    rpcs: [
      'https://rpc.mainnet.chain.robinhood.com',
      'https://robinhood-rpc.publicnode.com',
      'https://robinhood.drpc.org',
    ],
    explorer: 'https://robinhoodchain.blockscout.com',
  },
  [TESTNET]: {
    name: 'Robinhood Chain testnet',
    hex: '0xb626',
    rpcs: ['https://rpc.testnet.chain.robinhood.com'],
    explorer: 'https://explorer.testnet.chain.robinhood.com',
  },
};

export function isChainId(n: number): n is ChainId {
  return n === MAINNET || n === TESTNET;
}

/** Same-origin JSON-RPC relay (api/rpc.js, keyed upstream from the operator's environment). */
export const PROXY = '/api/rpc';

export const ISSUER_BEACON = '0xe10b6f6b275de231345c20d14ab812db62151b00';
export const ISSUER_IMPL = '0xb35490d6f9163de4f80d88dc75c3516eb64c5ae2';
export const BEACON_SLOT = '0xa3f0ad74e5423aebfd80d3ef4346578335a9a72aeaee59ff6cb3582b35133d50';

export const SEL = {
  latestRoundData: '0xfeaf968c',
  decimals: '0x313ce567',
  implementation: '0x5c60da1b',
  isOfficial: '0x77b81aac',
  describe: '0x806c40f1',
  // BellHook and BellConfig views (selectors from the forge artifacts of the product repository).
  getFee: '0x69516b74',
  gapBps: '0x7062e993',
  floatShareBps: '0xb15bed4c',
  isStale: '0xf461f6e7',
  poolInfo: '0x8cebd942',
  curve: '0x7165485d',
  floatGuard: '0xd886cfa8',
} as const;

// StockVerifier on Robinhood Chain mainnet (chain id 4663), deployed 2026-09-08, Sourcify exact match.
export const VERIFIER = '0x574cB84717b8aF7Df5F40a8344D783728317114f';
export const NVDA = '0xd0601CE157Db5bdC3162BbaC2a2C8aF5320D9EEC';

// Testnet 46630 hook-v2 stack, deployed 2026-09-23 (docs/deployments/testnet-46630-v2.json and
// docs/deployment.md "Broadcast (2026-09-23)" in the product repository; Sourcify exact match).
// T_HOOK is the single place the testnet hook address is written down. It is the fallback for the direct
// testnet read and the Deployed table; the hook card shows the address from /api/data/hook (the
// worker's readout) whenever that answers. On a hook redeploy, change it here, together with T_POOL.id
// (the pool id hashes the hook address) and T_CONFIG if BellConfig was redeployed too.
// The TimelockController 0x9fE818f439Bb00c6AD7ba401CC72C9425009E35e (2026-09-08) was reused.
export const T_HOOK = '0x187919f9F984D6EF18B95011c74f646e7D4b65D0';
export const T_CONFIG = '0x00F6A8416707d860b71d74ec52b10431ab0895eB';
/** Owner-only MockAggregator for TSLA (the testnet feed, set by hand). */
export const T_FEED = '0x67202817D46B13FcD8fB176e28999E4C504341d4';
/** MockUSDG ("Mock USDG", mUSDG, 6 decimals, fixed supply of 1,000,000 minted once). */
export const T_MUSDG = '0x6aFC3A9B044d73c40a09701b3079A96fB2609138';

// Mainnet 4663 hook-v2 stack, deployed 2026-09-23 (docs/deployments/mainnet-4663-v2.json in the product
// repository; Sourcify exact match). Curve (500, 50, 30000, 10800, 500, 25), as on testnet.
export const M_HOOK = '0xeF6c47Dc2999C19CeC6a06982283dd9dBD7cE5D0';
export const M_CONFIG = '0x9fE818f439Bb00c6AD7ba401CC72C9425009E35e';
/** TimelockController owning M_CONFIG, minimum delay 86,400 s. */
export const M_TIMELOCK = '0xC0E4b366D22BF00816Dc88288644C06FACA312d8';
/** NVDA/USDG pool id (dynamic fee, tickSpacing 60, hooks M_HOOK), initialised at block 70812670
 *  with no liquidity yet. */
export const M_POOL_ID = '0xa2f813a14f7c7c23a428b429f37204646ba9b66141e88f2d9ba3564c3fc149ad';

export interface PoolKey {
  id: string;
  currency0: string;
  currency1: string;
  fee: number;
  tickSpacing: number;
  hooks: string;
}

export const T_POOL: PoolKey = {
  id: '0x36341e736bffca4be333d12bc8a7739516f3d70b494781fb52b4cc257e59c201',
  // MockUSDG sorts below TSLA, so the quote is currency0 and the stock currency1 (hook.ts relies on that).
  // Testnet USDG 0x7E955252E15c84f5768B83c41a71F9eba181802F has a role-gated mint and no faucet, hence the mock.
  currency0: T_MUSDG, // MockUSDG (mUSDG)
  currency1: '0xC9f9c86933092BbbfFF3CCb4b105A4A94bf3Bd4E', // faucet TSLA
  fee: 0x800000,
  tickSpacing: 60,
  hooks: T_HOOK,
};

export const CAST_RPC = CHAINS[MAINNET].rpcs[0] ?? '';

export const explorerAddr = (addr: string, chainId: ChainId = MAINNET): string =>
  `${CHAINS[chainId].explorer}/address/${addr}`;

export const castDescribe = (addr: string): string =>
  `cast call ${VERIFIER} "describe(address)(bool,string,string,bytes32,uint256,bool,bool)" ${addr} --rpc-url ${CAST_RPC}`;
export const castIsOfficial = (addr: string): string =>
  `cast call ${VERIFIER} "isOfficial(address)(bool)" ${addr} --rpc-url ${CAST_RPC}`;

export const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
